import { User, type UserProfile } from 'oidc-client-ts'
import { httpJson } from '../../platform/abp/httpClient'
import { ABP_AUTH_TOKEN_STORAGE, BUXUS_API_BASE_URL } from '../../platform/abp/config'
import { oidcManager } from '../../platform/auth/oidcManager'

const ORIGINAL_SESSION_KEY = 'buxus-scenario-impersonation-original'
const TARGETS_CACHE_KEY = 'buxus-scenario-impersonation-targets'

export type ImpersonationTarget = {
  userId: string
  label: string
  roleName: string
  email: string
  /** Identity first and last name, when the account has one. */
  name: string
  roleNames: string[]
  /** Effective Buxus.Scenarios.* grants from the account’s roles. */
  grantedPermissions: string[]
}

type SavedSession = {
  accessToken: string
  oidc: string | null
}

function unwrapList(body: unknown): ImpersonationTarget[] {
  const rec = body && typeof body === 'object' ? (body as Record<string, unknown>) : null
  const raw = rec && 'result' in rec ? rec.result : body
  if (!Array.isArray(raw)) return []
  return raw.flatMap((row) => {
    if (!row || typeof row !== 'object') return []
    const item = row as Record<string, unknown>
    const userId = typeof item.userId === 'string' ? item.userId : ''
    const label = typeof item.label === 'string' ? item.label : ''
    const roleName = typeof item.roleName === 'string' ? item.roleName : ''
    const email = typeof item.email === 'string' ? item.email : ''
    const name = typeof item.name === 'string' ? item.name.trim() : ''
    const roleNames = Array.isArray(item.roleNames)
      ? item.roleNames.filter((name): name is string => typeof name === 'string' && name.length > 0)
      : roleName
        ? [roleName]
        : []
    const grantedPermissions = Array.isArray(item.grantedPermissions)
      ? item.grantedPermissions.filter(
          (name): name is string => typeof name === 'string' && name.length > 0,
        )
      : []
    if (!userId || !label) return []
    return [{ userId, label, roleName, email, name, roleNames, grantedPermissions }]
  })
}

export async function fetchImpersonationTargets(): Promise<ImpersonationTarget[]> {
  const saved = readSavedSession()
  const body = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/impersonation-targets`, {
    auth: !saved,
    bearer: saved?.accessToken,
  })
  const rows = unwrapList(body)
  try {
    sessionStorage.setItem(TARGETS_CACHE_KEY, JSON.stringify(rows))
  } catch {
    /* private mode */
  }
  return rows
}

export function cachedImpersonationTargets(): ImpersonationTarget[] {
  try {
    const raw = sessionStorage.getItem(TARGETS_CACHE_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    return Array.isArray(parsed) ? unwrapList(parsed) : []
  } catch {
    return []
  }
}

function decodeJwt(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const padded = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='))
    const parsed = JSON.parse(json) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export function currentAccessToken(): string | null {
  return localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim() || null
}

export function hasSavedImpersonationSession(): boolean {
  try {
    return !!sessionStorage.getItem(ORIGINAL_SESSION_KEY)
  } catch {
    return false
  }
}

function readSavedSession(): SavedSession | null {
  try {
    const raw = sessionStorage.getItem(ORIGINAL_SESSION_KEY)
    const saved = raw ? (JSON.parse(raw) as SavedSession) : null
    return saved?.accessToken ? saved : null
  } catch {
    return null
  }
}
function claimText(claims: Record<string, unknown> | null, key: string): string {
  const value = claims?.[key]
  return typeof value === 'string' ? value.trim() : ''
}

/** Email (or username) of the account Back restores. */
export function savedImpersonationReturnIdentity(): string {
  try {
    const saved = readSavedSession()
    if (!saved) return ''
    if (saved.oidc) {
      const profile = User.fromStorageString(saved.oidc).profile as Record<string, unknown>
      const fromProfile =
        claimText(profile, 'email') ||
        claimText(profile, 'preferred_username') ||
        claimText(profile, 'unique_name') ||
        claimText(profile, 'name')
      if (fromProfile) return fromProfile
    }
    const claims = decodeJwt(saved.accessToken ?? '')
    return (
      claimText(claims, 'email') ||
      claimText(claims, 'preferred_username') ||
      claimText(claims, 'unique_name') ||
      claimText(claims, 'name')
    )
  } catch {
    return ''
  }
}

async function adoptAccessToken(accessToken: string, refreshToken: string | undefined, expiresIn: number): Promise<void> {
  const existing = await oidcManager.getUser().catch(() => null)
  const claims = decodeJwt(accessToken) ?? {}
  const sub = typeof claims.sub === 'string' && claims.sub ? claims.sub : existing?.profile.sub ?? 'user'
  const profile = { ...(existing?.profile ?? {}), ...claims, sub } as UserProfile
  const user = new User({
    access_token: accessToken,
    refresh_token: refreshToken ?? existing?.refresh_token,
    id_token: existing?.id_token,
    token_type: 'Bearer',
    scope: existing?.scope,
    session_state: existing?.session_state ?? null,
    profile,
    expires_at: Math.floor(Date.now() / 1000) + (expiresIn > 0 ? expiresIn : 3600),
  })
  await oidcManager.storeUser(user)
  localStorage.setItem(ABP_AUTH_TOKEN_STORAGE, accessToken)
}

async function requestImpersonationToken(userId: string): Promise<{ accessToken: string; refreshToken?: string; expiresIn: number }> {
  const accessToken = currentAccessToken()
  if (!accessToken) throw new Error('Not signed in')
  const authority = oidcManager.settings.authority.replace(/\/$/, '')
  const body = new URLSearchParams({
    grant_type: 'Impersonation',
    client_id: oidcManager.settings.client_id,
    access_token: accessToken,
    UserId: userId,
  })
  if (oidcManager.settings.scope) body.set('scope', oidcManager.settings.scope)
  const tenantId = decodeJwt(accessToken)?.tenantid
  if (typeof tenantId === 'string' && tenantId) body.set('TenantId', tenantId)

  const res = await fetch(`${authority}/connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body,
  })
  const json = (await res.json().catch(() => null)) as {
    access_token?: string
    refresh_token?: string
    expires_in?: number
    error_description?: string
    error?: string
  } | null
  if (!res.ok || !json?.access_token) {
    throw new Error('Could not switch user.')
  }
  return {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresIn: typeof json.expires_in === 'number' ? json.expires_in : 3600,
  }
}

export async function impersonateUser(userId: string): Promise<void> {
  const fixtureToken = currentAccessToken()
  const fixtureUser = await oidcManager.getUser().catch(() => null)
  const saved = readSavedSession()
  if (!saved) {
    if (fixtureToken) {
      const original: SavedSession = { accessToken: fixtureToken, oidc: fixtureUser?.toStorageString() ?? null }
      try {
        sessionStorage.setItem(ORIGINAL_SESSION_KEY, JSON.stringify(original))
      } catch {
        /* private mode */
      }
    }
  } else {
    // ABP rejects impersonation from a token that is already impersonating.
    // Put the real login back, then grant the new fixture from that token.
    localStorage.setItem(ABP_AUTH_TOKEN_STORAGE, saved.accessToken)
    if (saved.oidc) {
      await oidcManager.storeUser(User.fromStorageString(saved.oidc))
    }
  }
  try {
    const next = await requestImpersonationToken(userId)
    await adoptAccessToken(next.accessToken, next.refreshToken, next.expiresIn)
  } catch (err) {
    if (saved) {
      if (fixtureToken) localStorage.setItem(ABP_AUTH_TOKEN_STORAGE, fixtureToken)
      if (fixtureUser) await oidcManager.storeUser(fixtureUser)
    }
    throw err
  }
  window.location.reload()
}

export async function returnFromImpersonation(): Promise<void> {
  const saved = readSavedSession()
  if (!saved?.accessToken) throw new Error('No original session')
  if (saved.oidc) {
    await oidcManager.storeUser(User.fromStorageString(saved.oidc))
  }
  localStorage.setItem(ABP_AUTH_TOKEN_STORAGE, saved.accessToken)
  try {
    sessionStorage.removeItem(ORIGINAL_SESSION_KEY)
  } catch {
    /* ignore */
  }
  window.location.reload()
}
